import React, { ReactElement, Suspense, SyntheticEvent } from 'react';
import { useDebouncedCallback } from 'use-debounce';

import {
  EditorState,
  ContentState,
  RichUtils,
  DraftHandleValue,
  getDefaultKeyBinding,
  Modifier,
  BlockMapBuilder,
  convertFromHTML,
  KeyBindingUtil,
  ContentBlock,
  BlockMap,
  CompositeDecorator,
} from 'draft-js';

import Editor, {
  composeDecorators,
  EditorPlugin,
  PluginEditorProps,
} from './DraftJSPlugins/draft-js-plugins-editor/src';
import { stateToMarkdown } from './DraftJSPlugins/draft-js-export-markdown/src';
import { stateFromMarkdown } from './DraftJSPlugins/draft-js-import-markdown/src';

import createAnchorPlugin from './DraftJSPlugins/draft-js-anchor-plugin/src';
import createInlineToolbarPlugin, {
  ToolbarChildrenProps,
} from './DraftJSPlugins/draft-js-inline-toolbar-plugin/src/index';
import createSideToolbarPlugin from './DraftJSPlugins/draft-js-side-toolbar-plugin/src';
import createLinkifyPlugin from './DraftJSPlugins/draft-js-linkify-plugin/src';
import createImagePlugin from './DraftJSPlugins/draft-js-image-plugin/src';
import createFocusPlugin from './DraftJSPlugins/draft-js-focus-plugin/src';
import createBlockDndPlugin from './DraftJSPlugins/draft-js-drag-n-drop-plugin/src';
import createToolbarPlugin, {
  Separator,
} from './DraftJSPlugins/draft-js-static-toolbar-plugin/src';
import createCTAPlugin from './DraftJSPlugins/draft-js-cta-plugin';
import {
  HeadlineOneButton,
  HeadlineTwoButton,
  HeadlineThreeButton,
  BlockquoteButton,
  UnorderedListButton,
  OrderedListButton,
  ItalicButton,
  BoldButton,
  UnderlineButton,
  DraftJsStyleButtonProps,
} from './DraftJSPlugins/draft-js-buttons/src';

import { EditorTheme } from './EditorTheme';
import { css } from '@emotion/react';
import PluginEditor from './DraftJSPlugins/draft-js-plugins-editor/src';
import { isHtmlFormatting, getMaxCharsMessage } from '$/utils/string';
import tw from 'twin.macro';
import {
  standardizedHtmlForPasting,
  standardizedMarkdownContent,
} from './DraftJSPlugins/draft-js-plugins-utils/src';
import { fieldRegex } from '$/shared/frontend/utils/urls/URLUtils';
import { isImageUrl } from '$/utils/image';
import { isEmptyMarkdownString } from '$/components/ui/EditableInput/utils';
import { CTAConfig } from '../Input/CTAButton/types';
import ImageButton from './DraftJSPlugins/draft-js-image-plugin/src/ImageButton';
import { LocalAsset } from '../Input/PageBuilder.types';
// import BombBombButton from './DraftJSPlugins/draft-js-bombbomb-plugin/BombBombButton';
import { Portal } from '$/components/Portal';
import FontSizeButton from './FontSizeButton';
import { markdownToText } from '$/shared/frontend/utils/MarkdownConverter';
import { stripAllHtmlTags } from '$/utils/html';
import { Fillable, Mentionable } from './types';
import { useHasChangedShallow } from '$/hooks/useHasChanged';
import LinkDecorator from './LinkDecorator';
import { AddSavedReplyButton } from '../../SavedReplies/AddSavedReplyButton';
import { AddMentionButton } from './AddMentionButton/AddMentionButton';
import { useMentionPlugin } from './AddMentionButton/useMentionPlugin';
import { useSavedReplyMentionPlugin } from '$/components/SavedReplies/useSavedReplyMentionPlugin';
import { useEmojiPlugin } from './useEmojiPlugin';

const BombBombVideoButton = React.lazy(
  () => import('./DraftJSPlugins/draft-js-bombbomb-plugin/BombBombButton')
);

const emptyState = '\n'; // TODO: Clean up markdown to not have so many \n

export type EditableInputTextAlignment = 'left' | 'center' | 'right';
export type EditableInputTextDirectionality = 'LTR' | 'RTL' | 'NEUTRAL';
export type EditableInputStaticToolbarPosition = 'top' | 'bottom';
export type EditableInputStaticToolbarType = 'edit' | 'chat.msg' | 'chat.email';

export interface EditableInputProps
  extends Omit<PluginEditorProps, 'onChange' | 'editorState' | 'onBlur'> {
  defaultValue?: string;
  disabled?: boolean;
  isPreview?: boolean;
  placeholder?: string;
  autoFocus?: boolean;
  withBorder?: boolean;
  value?: string;
  className?: string;
  mentions?: Mentionable[];
  showSavedReplies?: boolean;
  fillin?: Fillable[];
  showSideToolbar?: boolean;
  showInlineToolbar?: boolean;
  showStaticToolbar?: EditableInputStaticToolbarType | false;
  staticToolbarPos?: EditableInputStaticToolbarPosition;
  staticToolbarScale?: string;
  showEmojiToolbar?: boolean;
  allowPastedStyles?: boolean;
  editorRef?: React.MutableRefObject<PluginEditor | undefined>;
  showFocusBox?: boolean;
  noLinkify?: boolean;
  protocolUrlRequired?: boolean;
  noFormatting?: boolean;
  id?: string;
  maxChars?: number;
  preventMentionFields?: boolean;
  onHandleEnter?: (value: string | null) => boolean;
  onBlur?: (e: SyntheticEvent, value: string | null) => void;
  onChange?: (value: string) => void;
  handleLoaded?: () => void;
  ctaConfig?: CTAConfig;
  asset?: LocalAsset;
  isInsideModal?: boolean;
  linkToolbarDisabled?: boolean;
  isUrlInput?: boolean;
}

const KeysCommand = ['ctrl-b', 'ctrl-i', 'ctrl-u'];

const MentionMessage = `Using fields may increase message past 1600 characters`;

// parserOptions is different from the default parserOptions in MarkdownParser component
const parserOptions = {
  gfm: true,
  breaks: true,
  pedantic: false,
  smartLists: false,
  silent: false,
  langPrefix: 'lang-',
  xhtml: false,
  atomicImages: true,
};

const createEmptyState = (decorators?: CompositeDecorator): EditorState => {
  return EditorState.createEmpty(decorators);
};

const getEditorStateFromValue = (
  value: string,
  lastValue: string | null,
  noFormatting?: boolean,
  autoFocus?: boolean,
  protocolUrlRequired?: boolean,
  disabled?: boolean,
  decorators?: CompositeDecorator
): EditorState => {
  if (!value || value.length === 0) {
    let state = createEmptyState(decorators);
    // BUG: I think calling moveFocusToEnd is a bug here but its needed
    // whenver there is already a props.value and our parent passes in
    // props.value = '' to clear the field...
    if (value === '' && lastValue !== null && lastValue !== '') {
      state = EditorState.moveFocusToEnd(state);
    }
    return state;
  }
  let contentState: ContentState;
  if (isHtmlFormatting(value) && typeof document !== 'undefined') {
    // Make sure a <br/> causes a break
    const html = value.replace(/<br/g, '\u200b<br');

    const blocksFromHTML = convertFromHTML(html);
    contentState = ContentState.createFromBlockArray(
      blocksFromHTML.contentBlocks,
      blocksFromHTML.entityMap
    );
  } else {
    contentState = stateFromMarkdown(noFormatting ? markdownToText(value) : value, {
      parserOptions,
    });
  }

  if (disabled) {
    // Fix unknow url or anchor link on public mode
    return EditorState.createWithContent(contentState, LinkDecorator(protocolUrlRequired));
  }

  return autoFocus
    ? EditorState.moveFocusToEnd(EditorState.createWithContent(contentState, decorators))
    : EditorState.createWithContent(contentState, decorators);
};

const ErrorMessage: React.FC<{ messages: string[] }> = ({ messages }) => {
  return (
    <div
      className='ui red pointing basic label'
      css={css`
        position: absolute !important;
        display: table !important;
        top: 100%;
        left: 50%;
        transform: translateX(-50%);
        z-index: 10;
        width: 100%;
        word-break: normal;
        span {
          display: block;
          margin-bottom: 0.25rem;
          &:last-child {
            margin-bottom: 0;
          }
        }
      `}
    >
      {messages.map((message, index) => {
        return <span key={index}>{message}</span>;
      })}
    </div>
  );
};

export const EditableInput: React.FC<React.PropsWithChildren<EditableInputProps>> = ({
  disabled,
  isPreview,
  noLinkify,
  protocolUrlRequired,
  editorRef,
  placeholder,
  handleLoaded,
  withBorder,
  id,
  maxChars,
  preventMentionFields,
  showEmojiToolbar,
  showSavedReplies,
  ...props
}) => {
  const staticToolbarId = React.useRef(Date.now().toString());
  const hiddenDivRef = React.useRef<HTMLDivElement>(null);
  const [lastValue, setLastValue] = React.useState<string | null>(null);
  // Fix editor not load data on Reg page with full URL
  const [editorState, setEditorState] = React.useState(
    getEditorStateFromValue(
      props.value ?? emptyState,
      lastValue,
      props.noFormatting,
      props.autoFocus,
      protocolUrlRequired,
      disabled
    )
  );

  // DraftJS decorators will be null in some cases: clear message in ChatInput or switch between conversations,...
  // Using the `decorators` state to backup decorators of editor
  const [decorators, setDecorators] = React.useState<CompositeDecorator>();
  const inlineToolBarRef = React.useRef<React.ComponentType<ToolbarChildrenProps>>();

  const [loaded, setLoaded] = React.useState(false);
  const [isPlaceHolder, setIsPlaceHolder] = React.useState(
    () => !props.value || isEmptyMarkdownString(props.value)
  );
  const [isFocus, setIsFocus] = React.useState(false);
  const [isKeyPressing, setIsKeyPressing] = React.useState(false);

  const [errors, setErrors] = React.useState<string[] | null>();

  if (typeof document !== 'undefined') {
    document.body.style['scrollBehavior'] = 'smooth';
  }

  const [focusPlugin, blockDndPlugin, imagePlugin, ctaPlugin] = React.useMemo(() => {
    const focus = createFocusPlugin();
    const blockDnd = createBlockDndPlugin();

    const decorator = composeDecorators(focus.decorator, blockDnd.decorator);

    const image = createImagePlugin({ isPublish: disabled, decorator });

    const ctaDecorator = composeDecorators(focus.decorator);
    const cta = props.ctaConfig?.showCTA
      ? createCTAPlugin({
          isPublish: disabled,
          renderCTA: props.ctaConfig.renderCTA,
          decorator: ctaDecorator,
          onlyCustomCTA: props.ctaConfig.onlyCustomCTA,
          defaultText: props.ctaConfig.defaultText,
        })
      : undefined;

    return [focus, blockDnd, image, cta];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run once on load and not re-render when switching to preview mode

  const plugins = React.useMemo(() => {
    return {
      sideToolbar: disabled
        ? undefined
        : createSideToolbarPlugin({
            theme: {
              buttonStyles: EditorTheme.buttonStyles,
              blockTypeSelectStyles: EditorTheme.blockTypeSelectStyles,
              toolbarStyles: EditorTheme.sideToolbarStyles,
            },
          }),
      inlineToolbar: createInlineToolbarPlugin({
        theme: {
          buttonStyles: EditorTheme.buttonStyles,
          toolbarStyles: EditorTheme.inlineToolbarStyles,
        },
      }),
      toolbarPlugin: createToolbarPlugin(),
      linkPlugin: createAnchorPlugin({ linkTarget: '_blank' }),
      linkifyPlugin: noLinkify
        ? undefined
        : createLinkifyPlugin({ target: '_blank', fuzzyLink: !protocolUrlRequired }),
      blockDndPlugin,
      focusPlugin,
      imagePlugin,
      ctaPlugin,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on load

  const mentionPosition = props.showStaticToolbar === 'chat.msg' ? 'top' : undefined;
  const { mentionSuggestionsOpen, mentionSuggestions, mentionSuggestionsPlugin } = useMentionPlugin(
    {
      isKeyPressing,
      isUrlInput: props.isUrlInput,
      mentions: props.mentions,
      lastValue,
      position: mentionPosition,
    }
  );
  const { savedReplySuggestions, savedReplySuggestionsOpen, savedReplySuggestionsPlugin } =
    useSavedReplyMentionPlugin({
      enable: showSavedReplies,
      isKeyPressing,
      lastValue,
      position: mentionPosition,
    });

  const { emojiButton, emojiSuggestions, emojiPlugin, emojiSuggestionsOpen } = useEmojiPlugin({
    enable: Boolean(showEmojiToolbar),
    isRtl: props.textDirectionality === 'RTL',
  });

  const { noFormatting } = props;
  React.useEffect(() => {
    if (noFormatting) {
      props.showSideToolbar = false;
      props.showInlineToolbar = false;
      props.allowPastedStyles = false;
    }
  }, [noFormatting, props]);

  React.useEffect(() => {
    if (decorators) return;
    setDecorators(editorState.getDecorator());
  }, [decorators, editorState]);

  const { SideToolbar } = plugins.sideToolbar ?? { SideToolbar: null };
  const { InlineToolbar } = plugins.inlineToolbar;
  const { Toolbar } = plugins.toolbarPlugin;
  const { CTAButton } = plugins.ctaPlugin ?? { CTAButton: null };

  const enabledPlugins = [
    ...Object.values(plugins),
    mentionSuggestionsPlugin,
    savedReplySuggestionsPlugin,
    emojiPlugin,
  ].filter((plugin) => Boolean(plugin));

  const prepareNewValue = (value: string): string => {
    const maxError = getMaxCharsMessage(maxChars, value);
    const errorString = maxError ? [maxError] : [];
    if (preventMentionFields && maxError) {
      const fields = value.match(fieldRegex);
      if (typeof fields !== 'undefined' && fields !== null) {
        // When preventMentionFields is true and the message contains fields, we will show mention message warning
        errorString.push(MentionMessage);
      }
    }
    setErrors(errorString);
    return value;
  };

  const onChangeDebounced = useDebouncedCallback(
    () => {
      if (!loaded) return;

      let newValue = prepareNewValue(stateToMarkdown(editorState.getCurrentContent()));
      // Remove default character '\n' at the end of markdown string
      // match with old data and no need \n at the end of string
      newValue = newValue.replace(/\n$/g, '');
      if (
        (!lastValue && isEmptyMarkdownString(newValue)) ||
        (lastValue &&
          (lastValue.replace(/\n$/g, '') === newValue ||
            (isEmptyMarkdownString(lastValue) && isEmptyMarkdownString(newValue))))
      ) {
        return;
      }
      setLastValue(newValue);
      props.onChange && props.onChange(isEmptyMarkdownString(newValue) ? '' : newValue);
    },
    100,
    { trailing: true }
  );

  const handleKeyPress = React.useCallback(
    (e: React.KeyboardEvent): string | null => {
      setIsKeyPressing(true);

      if (props.noFormatting && KeyBindingUtil.hasCommandModifier(e)) {
        if (e.keyCode === 66) {
          return 'ctrl-b';
        } else if (e.keyCode === 73) {
          return 'ctrl-i';
        } else if (e.keyCode === 85) {
          return 'ctrl-u';
        }
      }

      if (e.keyCode === 13) {
        if (e.shiftKey) {
          // \n added automatically
          return getDefaultKeyBinding(e);
        }

        if (props.onHandleEnter) {
          // If debounce is pending, call it one last time
          onChangeDebounced.flush();
          const latestValue = stateToMarkdown(editorState.getCurrentContent());
          if (props.onHandleEnter(latestValue)) {
            return null;
          }
        }
      }

      return getDefaultKeyBinding(e);
    },
    [onChangeDebounced, editorState, props]
  );

  const valueChanged = useHasChangedShallow([props.value]);
  React.useEffect(() => {
    // Only run when: on loading or calling setValue, formHandlers?.reset from the parent
    // Not run when: enter text on this editor or clear all text with backspace
    if (
      !valueChanged ||
      (props.value && lastValue === props.value) || // case: enter text on this editor
      (!props.value && lastValue !== null && isEmptyMarkdownString(lastValue)) // case: clear all text with backspace, lastValue === null : onload
    ) {
      return;
    }
    // Don't send onChange if debouncing is still in effect
    onChangeDebounced.cancel();
    setLastValue(props.value ?? emptyState);
    const value = getEditorStateFromValue(
      props.value ?? emptyState,
      lastValue,
      props.noFormatting,
      props.autoFocus,
      protocolUrlRequired,
      disabled,
      decorators
    );
    setEditorState(value);
    if (props.value && !isEmptyMarkdownString(props.value)) {
      setIsPlaceHolder(false);
    }
    setLoaded(true);
    handleLoaded && handleLoaded();
    // Only run effect when a new value is passed in from client
  }, [
    onChangeDebounced,
    decorators,
    disabled,
    handleLoaded,
    lastValue,
    loaded,
    props.autoFocus,
    props.mentions,
    props.noFormatting,
    props.value,
    protocolUrlRequired,
    valueChanged,
  ]);

  const onChange = React.useCallback(
    (newEditorState: EditorState): void => {
      // We need to apply editor state's decorator when component is not loaded
      if (!loaded && newEditorState.getDecorator()) setEditorState(newEditorState);
      if (!loaded) return;
      setEditorState(newEditorState);
      onChangeDebounced();
    },
    [loaded, onChangeDebounced]
  );

  const handleKeyCommand = React.useCallback(
    (command: string, editorState: EditorState): DraftHandleValue => {
      if (props.noFormatting && KeysCommand.includes(command)) {
        return 'not-handled';
      }
      if (command === 'backspace') {
        const selectionState = editorState.getSelection();
        const anchorKey = selectionState.getAnchorKey();
        const currentContent = editorState.getCurrentContent();
        const currentContentBlock = currentContent.getBlockForKey(anchorKey);
        if (
          (currentContentBlock.getType() === 'unstyled' ||
            currentContentBlock.getType() === 'atomic') &&
          currentContentBlock.getCharacterList().some((cm) => {
            const type = cm && cm.getEntity() && currentContent.getEntity(cm.getEntity()).getType();
            return type === 'IMAGE';
          })
        ) {
          return 'not-handled';
        }
      }

      const newState = RichUtils.handleKeyCommand(editorState, command);

      if (newState) {
        onChange(newState);
        return 'handled';
      }

      return 'not-handled';
    },
    [onChange, props.noFormatting]
  );

  const handleFocus = React.useCallback(
    (e: SyntheticEvent): void => {
      setIsFocus(true);
      setIsPlaceHolder(false);

      props.onFocus && props.onFocus(e);
    },
    [props]
  );

  const handleBlur = React.useCallback(
    (evt: SyntheticEvent): void => {
      setIsFocus(false);
      if (props.value && !isEmptyMarkdownString(props.value)) {
        setIsPlaceHolder(false);
      } else {
        setIsPlaceHolder(true);
      }

      if (props.onBlur) {
        const latestValue = stateToMarkdown(editorState.getCurrentContent());
        props.onBlur(evt, latestValue);
      }
    },
    [editorState, props]
  );

  const contentStateConverter = React.useCallback(
    (contentState: ContentState): Immutable.Map<string, unknown> => {
      // changes block type of images to 'atomic'
      const newBlockMap = contentState
        .getBlockMap()
        .map((block: ContentBlock | undefined): BlockMap | undefined => {
          const entityKey = block?.getEntityAt(0);
          if (block && entityKey) {
            const entityBlock = contentState.getEntity(entityKey);
            const entityType = entityBlock.getType();
            switch (entityType) {
              case 'IMAGE': {
                const newBlock = block.merge({
                  type: 'atomic',
                  text: ' ',
                });
                return newBlock;
              }
              default:
                return block;
            }
          }
          return block;
        });

      const newContentState = contentState.set('blockMap', newBlockMap);
      return newContentState;
    },
    []
  );

  const handlePastedText = React.useCallback(
    (text: string, html: string, editorState: EditorState): DraftHandleValue => {
      const content = html || text;
      if (props.allowPastedStyles && content) {
        const contentUrl = stripAllHtmlTags(content).trim();
        const res = isImageUrl(contentUrl);
        res.then((isImage) => {
          if (isImage) {
            // Add image
            const newState = imagePlugin.addImage(editorState, contentUrl);
            onChange(newState);
            return true;
          }
          // Add normal content
          const blocksFromHTML = convertFromHTML(standardizedHtmlForPasting(content));

          const emptyState = createEmptyState(decorators);
          const fragment = BlockMapBuilder.createFromArray(
            blocksFromHTML.contentBlocks.length
              ? blocksFromHTML.contentBlocks
              : emptyState.getCurrentContent().getBlocksAsArray()
          );

          const newContentState = Modifier.replaceWithFragment(
            editorState.getCurrentContent(),
            editorState.getSelection(),
            fragment
          );
          // TODO: use to render url of anchor link with href is { field name }
          // Render dashes and number of 'ordered-list-item' and 'unordered-list-item' when pasting
          const newValue = standardizedMarkdownContent(
            stateToMarkdown(contentStateConverter(newContentState))
          );
          const newEditorState = getEditorStateFromValue(
            newValue,
            lastValue,
            props.noFormatting,
            props.autoFocus,
            protocolUrlRequired,
            disabled,
            decorators
          );
          let newState = EditorState.push(
            editorState,
            newEditorState.getCurrentContent(),
            'insert-fragment'
          );
          newState = EditorState.forceSelection(newState, newState.getSelection());
          onChange(newState);
          return false;
        });
        return 'handled';
      }
      return 'not-handled';
    },
    [
      contentStateConverter,
      decorators,
      disabled,
      imagePlugin,
      lastValue,
      onChange,
      props.allowPastedStyles,
      props.autoFocus,
      props.noFormatting,
      protocolUrlRequired,
    ]
  );

  const editorCallback = React.useCallback(
    (node?: PluginEditor | null) => {
      if (!node) return;

      if (editorRef) {
        // Manually set editorRef.current
        editorRef.current = node;
      }

      // When we create the Editor node, because we call
      // EditorState.moveFocusToEnd in the init effect, it can grab focus
      // reason without telling us.  So on init we make sure we start
      // with a blur state - and then force focus if we want

      if (props.autoFocus) {
        setTimeout(() => {
          try {
            // Do we still need this?
            // node.blur();
            node.focus();
          } catch {}
        }, 10);
      } else {
        // DD: Do we need this at all?
        setTimeout(() => {
          try {
            node.blur();
          } catch {}
        }, 10);
      }
    },
    [editorRef, props.autoFocus]
  );

  const toolbar = React.useMemo((): JSX.Element | null => {
    switch (props.showStaticToolbar) {
      case 'chat.email':
      case 'chat.msg':
        return (
          <Toolbar position={props.staticToolbarPos ?? 'top'}>
            {(externalProps): ReactElement => (
              <>
                {emojiButton}
                <BoldButton {...(externalProps as DraftJsStyleButtonProps)} />
                <ItalicButton {...(externalProps as DraftJsStyleButtonProps)} />
                <Separator />
                <plugins.linkPlugin.LinkButton
                  {...externalProps}
                  inStatic
                  inlineToolBarRef={inlineToolBarRef}
                />
                {props.mentions && (
                  <AddMentionButton
                    {...externalProps}
                    mentions={props.mentions}
                    isRtl={props.textDirectionality === 'RTL'}
                  />
                )}
                {showSavedReplies && (
                  <AddSavedReplyButton
                    {...externalProps}
                    isRtl={props.textDirectionality === 'RTL'}
                  />
                )}
                {/* sda */}
                <div id='send-button-area' className='flex flex-row flex-1 justify-end' />
              </>
            )}
          </Toolbar>
        );

      case 'edit':
        return (
          <Toolbar position={props.staticToolbarPos ?? 'top'}>
            {(externalProps): ReactElement => (
              <>
                <HeadlineOneButton {...(externalProps as DraftJsStyleButtonProps)} />
                <HeadlineTwoButton {...(externalProps as DraftJsStyleButtonProps)} />
                <UnorderedListButton {...(externalProps as DraftJsStyleButtonProps)} />
                <OrderedListButton {...(externalProps as DraftJsStyleButtonProps)} />

                <Separator />

                <BoldButton {...(externalProps as DraftJsStyleButtonProps)} />
                <ItalicButton {...(externalProps as DraftJsStyleButtonProps)} />
                <UnderlineButton {...(externalProps as DraftJsStyleButtonProps)} />
                <FontSizeButton />
                <plugins.linkPlugin.LinkButton
                  {...externalProps}
                  inStatic
                  inlineToolBarRef={inlineToolBarRef}
                />

                <Separator />

                <ImageButton
                  store={externalProps}
                  ownTheme={externalProps.theme}
                  asset={props.asset}
                  {...externalProps}
                />
                <Suspense>
                  <BombBombVideoButton {...externalProps} />
                </Suspense>

                {CTAButton && <CTAButton {...(externalProps as DraftJsStyleButtonProps)} />}
              </>
            )}
          </Toolbar>
        );
    }

    return null;
  }, [
    CTAButton,
    Toolbar,
    plugins,
    props.asset,
    props.showStaticToolbar,
    props.staticToolbarPos,
    props.mentions,
    showSavedReplies,
    emojiButton,
    props.textDirectionality,
  ]);

  return (
    <Wrapper
      isFocus={isFocus}
      noLinkify={Boolean(noLinkify)}
      showFocusBox={Boolean(props.showFocusBox)}
      isPlaceHolder={isPlaceHolder}
      className={props.className}
      css={[
        css`
          .staticToolbar {
            ${tw`font-body`}
          }
        `,
        withBorder
          ? css`
              ${tw`border border-color-input-box rounded-input`}
              padding: 0.67857143em 1em;
            `
          : undefined,
        editorState.getCurrentContent().hasText() &&
          css`
            .public-DraftEditorPlaceholder-root {
              display: none;
            }
          `,
      ]}
      setIsKeyPressing={setIsKeyPressing}
      disabled={disabled || isPreview}
      id={id}
    >
      {props.staticToolbarPos !== 'bottom' && <div id={staticToolbarId.current} />}
      <Editor
        stripPastedStyles={!props.allowPastedStyles}
        spellCheck={true}
        readOnly={disabled}
        placeholder={!disabled ? placeholder : ''}
        {...props}
        ref={editorCallback}
        editorState={editorState}
        onChange={onChange}
        handleKeyCommand={handleKeyCommand}
        onFocus={handleFocus}
        onBlur={handleBlur}
        keyBindingFn={
          !mentionSuggestionsOpen && !savedReplySuggestionsOpen && !emojiSuggestionsOpen
            ? (e): string | null => handleKeyPress(e)
            : undefined
        }
        handleReturn={props.noFormatting ? (): DraftHandleValue => 'handled' : undefined}
        handlePastedText={handlePastedText}
        plugins={enabledPlugins as EditorPlugin[]}
      />
      {!disabled && props.showSideToolbar && SideToolbar && (
        <SideToolbar isInsideModal={Boolean(props?.isInsideModal)}>
          {(externalProps: ToolbarChildrenProps): ReactElement => (
            <>
              <HeadlineOneButton {...(externalProps as DraftJsStyleButtonProps)} />
              <HeadlineTwoButton {...(externalProps as DraftJsStyleButtonProps)} />
              <HeadlineThreeButton {...(externalProps as DraftJsStyleButtonProps)} />
              <BlockquoteButton {...(externalProps as DraftJsStyleButtonProps)} />
              <UnorderedListButton {...(externalProps as DraftJsStyleButtonProps)} />
              <OrderedListButton {...(externalProps as DraftJsStyleButtonProps)} />
              {CTAButton && <CTAButton {...(externalProps as DraftJsStyleButtonProps)} />}
            </>
          )}
        </SideToolbar>
      )}
      {!disabled && props.showInlineToolbar && !props.noFormatting && (
        <InlineToolbar
          isKeyPressing={isKeyPressing}
          asset={props.asset}
          onlyCustomCta={props.ctaConfig?.onlyCustomCTA}
          hasStaticToolbar={Boolean(props?.showStaticToolbar)}
          isInsideModal={Boolean(props?.isInsideModal)}
          linkToolbarDisabled={Boolean(props?.linkToolbarDisabled)}
          onlyOverrideContent={props.showStaticToolbar === 'chat.msg'}
          editorId={id}
          ref={inlineToolBarRef}
        >
          {(externalProps: ToolbarChildrenProps): ReactElement => (
            <>
              <BoldButton {...(externalProps as DraftJsStyleButtonProps)} />
              <ItalicButton {...(externalProps as DraftJsStyleButtonProps)} />
              {/* <UnderlineButton {...(externalProps as DraftJsStyleButtonProps)} /> */}
              <plugins.linkPlugin.LinkButton
                {...externalProps}
                linkToolbarDisabled={Boolean(props?.linkToolbarDisabled)}
              />
            </>
          )}
        </InlineToolbar>
      )}

      {props.staticToolbarPos === 'bottom' && <div id={staticToolbarId.current} />}

      {!disabled && props.showStaticToolbar && (
        <Portal containerId={staticToolbarId.current}>
          <div
            css={[
              props.staticToolbarScale
                ? css`
                    transform: scale(${props.staticToolbarScale});
                  `
                : undefined,
            ]}
          >
            {toolbar}
          </div>
        </Portal>
      )}
      {emojiSuggestions}
      {mentionSuggestions}
      {savedReplySuggestions}

      {/* <AlignmentTool /> */}

      {errors && errors.length > 0 && <ErrorMessage messages={errors} />}
      <div className='absolute w-0 h-0' ref={hiddenDivRef}></div>
    </Wrapper>
  );
};

const Wrapper: React.FC<{
  isFocus: boolean;
  noLinkify: boolean;
  showFocusBox: boolean;
  isPlaceHolder: boolean;
  className?: string;
  id?: string;
  disabled?: boolean;
  setIsKeyPressing: (value: boolean) => void;
  children: React.ReactNode;
}> = ({
  children,
  noLinkify,
  isPlaceHolder,
  className,
  isFocus,
  disabled,
  showFocusBox,
  setIsKeyPressing,
  id,
}) => {
  // We use mounted to only render the Editor once the parent DOM
  // node is available otherwise in Safari there's a flex bug which
  // calculates the width of the editor to be 0px

  const [mounted, setMounted] = React.useState(disabled ? true : false);

  const ref = React.useCallback((node?: HTMLElement | null) => {
    if (node) {
      setMounted(true);
    }
  }, []);

  return (
    <div
      ref={ref}
      id={id}
      className={`
        EditableInput custom-editor
        ${disabled ? 'is-disabled' : 'cursor-text'}
        ${noLinkify ? 'noLinkify' : ''}
        ${isFocus ? 'has-focus' : ''}
        ${isPlaceHolder ? 'is-placeholder' : ''}
        ${className}
      `}
      css={css`
        min-height: 1px;
        ${isFocus && showFocusBox
          ? 'box-shadow: rgb(99, 139, 143) 0px 0px 2px 2px !important;'
          : ''}
        &.is-placeholder:not(.default-focus) {
          .DraftEditor-root {
            .DraftEditor-editorContainer {
              position: absolute;
              top: 0;
              left: 0;
              right: 0;
              bottom: 0;
              z-index: 2;
            }
          }
        }
        h1 {
          ${tw`font-medium outline-none text-2xl md:text-4xl my-3/4e`}
        }
        h2 {
          ${tw`font-medium outline-none text-xl md:text-3xl my-1/2e`}
        }
        h3 {
          ${tw`font-medium outline-none text-lg md:text-2xl my-1/2e`}
        }
      `}
      onMouseDown={(): void => setIsKeyPressing(false)}
    >
      {mounted ? children : <div className='block'>&nbsp;</div>}
    </div>
  );
};

export const MarkdownInput: React.FC<EditableInputProps> = (props) => {
  return (
    <EditableInput
      css={css`
        ${tw`text-input-field`}
        background: #fff;
        border-width: 1px;
        transition: background;
        padding: 0.5rem 0.75rem !important;
        z-index: +1; // z-index can't be higher than pop-ups like the color picker
        .DraftEditor-root {
          width: 100%;
          min-height: 4.5rem;
          overflow-x: hidden;
          overflow-y: auto;
          .public-DraftEditorPlaceholder-root {
            position: absolute !important;
            .public-DraftEditorPlaceholder-inner {
              white-space: pre-wrap !important;
            }
          }
        }
        &.is-placeholder {
          .DraftEditor-root {
            height: 100%;
            .DraftEditor-editorContainer {
              height: 100%;
              .public-DraftEditor-content {
                position: absolute;
                left: 0;
                top: 0;
                right: 0;
                bottom: 0;
              }
            }
          }
        }
      `}
      withBorder
      allowPastedStyles
      {...props}
    />
  );
};

export default EditableInput;
